import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, fileUrl, formatPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Page } from "@/components/layout/Layout";
import { Loading, ErrorBox, Empty } from "@/components/common/States";
import { CompareToggle, ListingCard } from "@/components/common/Cards";
import SpecTable from "@/components/common/SpecTable";
import ProductReviews from "@/components/reviews/ProductReviews";
import SEO from "@/components/common/SEO";

function CorrectionDialog({ product }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ field_key: product.category_info.fields[0].key, proposed_value: "", note: "" });
  if (!user) return null;
  const submit = async () => {
    try { toast.success((await api.post(`/products/${product.id}/corrections`, f)).data.message); setOpen(false); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="bg-transparent" data-testid="suggest-correction-button">Düzeltme öner</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Katalog düzeltme önerisi</DialogTitle></DialogHeader>
        <p className="text-sm text-zinc-500">Öneriniz yönetici onayından sonra kataloğa uygulanır.</p>
        <Select value={f.field_key} onValueChange={(v) => setF({ ...f, field_key: v })}>
          <SelectTrigger data-testid="correction-field-select"><SelectValue /></SelectTrigger>
          <SelectContent>{product.category_info.fields.map((x) => <SelectItem key={x.key} value={x.key}>{x.label}</SelectItem>)}</SelectContent>
        </Select>
        <Input data-testid="correction-value-input" placeholder="Doğru değer" value={f.proposed_value} onChange={(e) => setF({ ...f, proposed_value: e.target.value })} />
        <Textarea data-testid="correction-note-input" placeholder="Kaynak / açıklama" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
        <Button onClick={submit} data-testid="correction-submit-button" className="bg-[#0071e3] text-white">Gönder</Button>
      </DialogContent>
    </Dialog>
  );
}

export default function ProductDetail() {
  const { id } = useParams();
  const { data: p, isLoading, error } = useQuery({ queryKey: ["product", id], queryFn: async () => (await api.get(`/products/${id}`)).data });
  const { data: listings } = useQuery({ queryKey: ["listings", "product", id], queryFn: async () => (await api.get(`/listings?product_id=${id}`)).data });
  if (isLoading) return <Loading />;
  if (error) return <Page><ErrorBox message={errMsg(error)} /></Page>;
  return (
    <Page>
      <div className="grid gap-8 lg:grid-cols-[1fr_1.2fr]">
        <div className="panel aspect-[4/3] overflow-hidden">{p.images?.[0] ? <img src={fileUrl(p.images[0])} alt={p.title} loading="lazy" decoding="async" className="h-full w-full object-cover" /> : <div className="grid-bg grid h-full place-items-center text-sm text-zinc-500">Ürün görseli henüz eklenmedi</div>}</div>
        <div className="space-y-4">
          <p className="eyebrow">{p.category_info.name} · {p.brand}</p>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold" data-testid="product-title">{p.model}{p.revision && <span className="ml-2 rounded bg-black/[0.05] px-2 font-mono text-lg">{p.revision}</span>}</h1>
          {p.other_revisions?.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="other-revisions">
              <span className="text-zinc-500">Diğer revizyonlar (ayrı yorum ve özellikler):</span>
              {p.other_revisions.map((r) => <Link key={r.id} to={`/katalog/${r.id}`} className="rounded border border-black/[0.08] px-2 font-mono text-xs hover:border-[#0071e3]">{r.revision || "Revizyonsuz"}</Link>)}
            </div>
          )}
          {p.description && <p className="text-zinc-700">{p.description}</p>}
          <div className="flex flex-wrap gap-3 text-sm">
            <span className="panel px-3 py-2" data-testid="product-active-listings">{p.active_listings} aktif ilan</span>
            {p.min_price_try != null && <span className="panel px-3 py-2">En düşük: {formatPrice(p.min_price_try)}</span>}
          </div>
          <div className="flex flex-wrap gap-2">
            <CompareToggle type="product" id={p.id} title={p.title} category={p.category} />
            <CorrectionDialog product={p} />
            <Button asChild size="sm" className="bg-[#0071e3] text-white" data-testid="sell-this-button"><Link to="/ilan-ver">Bu ürünü sat</Link></Button>
          </div>
          {p.is_test_data && <p className="text-xs text-zinc-400">Örnek katalog kaydı (test verisi)</p>}
        </div>
      </div>
      <div className="mt-10 grid gap-10 lg:grid-cols-2">
        <section><h2 className="mb-3 font-display text-xl font-semibold">Katalog teknik özellikleri</h2><SpecTable fields={p.category_info.fields} specs={p.specs} /></section>
        <ProductReviews productId={p.id} productTitle={p.title} />
      </div>
      <section className="mt-12">
        <h2 className="mb-4 font-display text-xl font-semibold">Bu modelin ilanları</h2>
        {listings?.items?.length ? <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{listings.items.map((l) => <ListingCard key={l.id} item={l} />)}</div>
          : <Empty title="Bu model için aktif ilan yok" hint="Yine de karşılaştırmaya ekleyebilirsiniz" />}
      </section>
    </Page>
  );
}
